import {
  DetailedHTMLProps,
  InputHTMLAttributes,
  useCallback,
  useId,
  useRef,
  useState
} from 'react';

type FloatingLabelInputSize = 'small' | 'medium' | 'large';
type FloatingLabelInputVariant = 'default' | 'outlined' | 'filled' | 'minimal';

interface FloatingNumberInputProps extends Omit<
  DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>,
  'placeholder' | 'size'
> {
  placeholder: string;
  label?: string;
  size?: FloatingLabelInputSize;
  variant?: FloatingLabelInputVariant;
  containerClassName?: string;
  inputClassName?: string;
  labelClassName?: string;
  error?: string;
  showLabel?: boolean;
  onChangeHandler: (value: number) => void;
}

const FloatingNumberInput = ({
  placeholder,
  label,
  size = 'medium',
  variant = 'default',
  containerClassName = '',
  inputClassName = '',
  labelClassName = '',
  error,
  value,
  defaultValue,
  showLabel = true,
  id,
  onChangeHandler,
  onBlur,
  onFocus,
  ...rest
}: FloatingNumberInputProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const generatedId = useId();
  const inputId = id || generatedId;
  const [hasFocus, setHasFocus] = useState(false);

  const sizeClasses = {
    small: {
      padding: '~px-1.5/2 ~py-0.5/1',
      text: '~text-[0.625rem]/xs',
      label: '~text-[0.55rem]/[0.63rem]'
    },
    medium: {
      padding: '~px-3/4 ~py-2/3',
      text: '~text-xs/base',
      label: '~text-[0.6rem]/xs'
    },
    large: {
      padding: '~px-4/5 ~py-3/4',
      text: '~text-base/lg',
      label: '~text-xs/sm'
    }
  };

  const variantClasses = {
    default: {
      border: 'border-secondary-400/60',
      bg: 'bg-white dark:bg-secondary-dark-300',
      focusBorder:
        'focus:border-primary-500 focus:ring-2 focus:ring-primary-500/30',
      hoverBorder: 'hover:border-primary-400',
      labelBg: 'bg-white dark:bg-secondary-dark-300',
      shadow: 'shadow-sm'
    },
    outlined: {
      border: 'border-2 border-primary-300/40 dark:border-primary-400/30',
      bg: 'bg-transparent',
      focusBorder:
        'focus:border-primary-500 focus:border-2 focus:ring-2 focus:ring-primary-500/30',
      hoverBorder: 'hover:border-primary-400',
      labelBg: 'bg-white dark:bg-secondary-dark-300',
      shadow: 'shadow-sm'
    },
    filled: {
      border: 'border-0',
      bg: 'bg-primary-50 dark:bg-secondary-dark-300',
      focusBorder: 'focus:ring-2 focus:ring-primary-500/50',
      hoverBorder: 'hover:bg-primary-100',
      labelBg: 'bg-primary-50 dark:bg-secondary-dark-300',
      shadow: 'shadow-sm'
    },
    minimal: {
      border:
        'border-b border-primary-300/40 dark:border-primary-400/30 rounded-none',
      bg: 'bg-transparent',
      focusBorder:
        'focus:border-b-2 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20',
      hoverBorder: 'hover:border-primary-400',
      labelBg: 'bg-white dark:bg-secondary-dark-300',
      shadow: ''
    }
  };

  const currentSize = sizeClasses[size];
  const currentVariant = variantClasses[variant];

  const shouldShowLabel = !hasFocus && value === '';
  const labelText = label || placeholder;

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = e.target.value;

      if (inputValue === '') {
        onChangeHandler(0);
        return;
      }

      // Remove any decimal point or comma that might have been pasted
      const wholeNumberValue = inputValue.replace(/[.,]/g, '');

      if (wholeNumberValue !== inputValue) {
        e.target.value = wholeNumberValue;
      }

      !isNaN(Number(wholeNumberValue)) &&
        onChangeHandler(Number(wholeNumberValue));
    },
    [onChangeHandler]
  );

  return (
    <div className={`relative w-full ${containerClassName}`}>
      {showLabel && (
        <label
          htmlFor={inputId}
          className={`absolute transition-all duration-200 ease-in-out  pointer-events-none z-10 ${
            shouldShowLabel
              ? `top-1/2 ~left-3/4 ${currentSize.text} -translate-y-1/2 opacity-70`
              : `top-0 ~left-2/3 ~px-1/1.5 ${currentSize.label} -translate-y-1/2 ${currentVariant.labelBg} opacity-100 text-gray-500 dark:text-secondary-dark-900`
          } ${labelClassName}`}>
          {labelText}
        </label>
      )}

      <input
        ref={inputRef}
        id={inputId}
        value={value}
        defaultValue={defaultValue}
        placeholder={shouldShowLabel ? '' : placeholder}
        className={`w-full ${currentSize.padding} ${variant === 'minimal' ? '' : 'rounded-md'} border-2 ${
          error
            ? 'border-red-400 focus:border-red-500 focus:ring-red-400/30'
            : `${currentVariant.border} ${currentVariant.focusBorder} ${currentVariant.hoverBorder || ''}`
        } ${currentVariant.bg} ${currentVariant.shadow} ${currentSize.text} text-black dark:text-white placeholder-transparent focus:outline-none transition-all duration-200 ${inputClassName}`}
        onFocus={(event) => {
          setHasFocus(true);
          onFocus?.(event);
        }}
        onBlur={(event) => {
          setHasFocus(false);
          onBlur?.(event);
        }}
        onChange={handleChange}
        {...rest}
      />

      {error && (
        <small className='absolute -bottom-4 left-0 ~text-[0.62rem]/[0.675rem] text-red-600 animate-fadeIn w-max'>
          {error}
        </small>
      )}
    </div>
  );
};

export default FloatingNumberInput;
